import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import {
  EhDataTableShell,
  HeroHeaderLogo,
  SortableHeader,
  TableSortResetBar,
  sortRowsByKey,
  useTableSort,
} from "@/components/dataTable";
import type { HoursUsageEntry } from "@/pages/projekte/hoursUsage";
import { HoursUsageCell } from "@/pages/projekte/HoursUsageCell";
import {
  PROJECT_ACTION_COL_WIDTH,
  normalizeTimesheetStatus,
  type HeroCustomField,
  type TimesheetBadge as ProjectTimesheetBadge,
} from "@/pages/projekte/columns";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import {
  useCurrentUser,
  userIsAbrechnung,
  userIsAnrechnung,
  userIsBuero,
  userIsManagement,
  type CurrentUser,
} from "@/lib/auth";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { getAuftragSharepointWebUrl } from "@/lib/projects";
import { toast } from "@/lib/toast";
import type { TimesheetSummary } from "@/lib/timesheets";
import { markTimesheetAtContact } from "@/lib/timesheets";
import {
  formatInvoiceValue,
  getBillingQuestionBadgeClass,
  hasOpenBillingQuestions,
  shouldShowBillingQuestionBadge,
  getContactAgeBadgeConfig,
  getTimesheetDisplayNumber,
  getTimesheetStatus,
  shouldHighlightBillingQuestionRow,
  statusBadgeClass,
  timesheetStatusLabel,
} from "@/pages/stundennachweise/timesheetFilterLogic";
import {
  clampTimesheetColumnWidth,
  formatTimesheetPeriod,
  formatTimesheetProjectLabel,
  getTimesheetColumnDefaultWidth,
  getTimesheetColumnLabel,
  getTimesheetFolderPath,
  resolveTimesheetHeroProjectId,
  getTimesheetActionColWidth,
  isTimesheetActionColumn,
  renderTimesheetProjectCellValue,
  timesheetHasFolder,
  timesheetRowAsOrder,
} from "@/pages/stundennachweise/columns";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
const HERO_PROJECT_BASE = "https://login.hero-software.de/partner/Projects/view/";
const HERO_LOGO_LIGHT = "/static/img/hero-logo.png";
const HERO_LOGO_DARK = "/static/img/hero-logo-dark.png";
const HERO_LEGACY_LOGO_LIGHT = "/static/img/plancraft-logo.png";
const HERO_LEGACY_LOGO_DARK = "/static/img/plancraft-logo-dark.png";

export interface TimesheetTableProps {
  rows: TimesheetSummary[];
  user: CurrentUser | null;
  loading: boolean;
  columns: string[];
  heroFields?: HeroCustomField[];
  hoursUsageById?: Record<number, HoursUsageEntry>;
  widths?: Record<string, number>;
  onRowOpen: (row: TimesheetSummary) => void;
  onContactToggled?: (timesheetId: number, atContact: boolean) => void;
  onColumnWidthChange?: (key: string, width: number) => void;
  emptyText?: string;
}

/**
 * Anzahl der Zeilen, die sofort gerendert werden (deckt typischerweise den
 * sichtbaren Bereich ab). Der Rest wird nach dem ersten Paint nachgereicht,
 * damit die Tabelle bei großen Listen sofort sichtbar ist.
 */
const PROGRESSIVE_INITIAL_ROWS = 40;
const PROGRESSIVE_STEP_ROWS = 200;

/**
 * Gibt zunächst nur einen kleinen Anfangsausschnitt der Zeilen zurück und füllt
 * den Rest nach dem ersten Paint in Frame-Batches auf. So blockiert das
 * Rendering großer Listen nicht die erste sichtbare Darstellung; Filtern/
 * Sortieren setzt den Ausschnitt zurück (neue Array-Identität).
 */
function useProgressiveRows<T>(rows: T[]): T[] {
  const [limit, setLimit] = useState(PROGRESSIVE_INITIAL_ROWS);

  useEffect(() => {
    setLimit(PROGRESSIVE_INITIAL_ROWS);
    if (rows.length <= PROGRESSIVE_INITIAL_ROWS) return;

    let frame = 0;
    const grow = () => {
      setLimit((prev) => {
        const next = prev + PROGRESSIVE_STEP_ROWS;
        if (next < rows.length) {
          frame = window.requestAnimationFrame(grow);
        }
        return next;
      });
    };
    frame = window.requestAnimationFrame(grow);
    return () => window.cancelAnimationFrame(frame);
  }, [rows]);

  return useMemo(
    () => (rows.length > limit ? rows.slice(0, limit) : rows),
    [rows, limit],
  );
}

function useIsDesktopViewport(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 768px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

export function TimesheetTable({
  rows,
  user,
  loading,
  columns,
  heroFields = [],
  hoursUsageById = {},
  widths = {},
  onRowOpen,
  onContactToggled,
  onColumnWidthChange,
  emptyText = "Keine Stundennachweise gefunden.",
}: TimesheetTableProps) {
  const showElectronFolder = userIsBuero(user) && isElectronWithOpenFolder();
  const isDesktop = useIsDesktopViewport();
  const { sort, toggle, reset } = useTableSort();
  const sortedRows = useMemo(
    () => sortRowsByKey(rows, sort, (row, key) => getTimesheetSortValue(row, key, heroFields)),
    [heroFields, rows, sort],
  );
  const visibleRows = useProgressiveRows(sortedRows);

  // Nur die zum Viewport passende Variante rendern – sonst würde z. B. am
  // Desktop zusätzlich die komplette Mobile-Liste (alle Zeilen) ins DOM gebaut.
  if (isDesktop) {
    return (
      <DesktopTable
        rows={visibleRows}
        columns={columns}
        heroFields={heroFields}
        hoursUsageById={hoursUsageById}
        widths={widths}
        user={user}
        loading={loading}
        emptyText={emptyText}
        showElectronFolder={showElectronFolder}
        onRowOpen={onRowOpen}
        onContactToggled={onContactToggled}
        onColumnWidthChange={onColumnWidthChange}
        sort={sort}
        onSort={toggle}
        onSortReset={reset}
      />
    );
  }

  return (
    <MobileCards
      rows={visibleRows}
      user={user}
      loading={loading}
      emptyText={emptyText}
      onRowOpen={onRowOpen}
    />
  );
}

interface TimesheetDesktopTheadProps {
  dataColumns: string[];
  actionColumns: string[];
  actionColWidths: Record<string, number>;
  actionRightOffsets: Record<string, number>;
  heroFields: HeroCustomField[];
  showElectronFolder: boolean;
  sort: { key: string; direction: "asc" | "desc" } | null;
  onSort: (key: string) => void;
  activeResize: string | null;
  onResizePointerDown: (e: React.PointerEvent, colKey: string, idx: number) => void;
  omitResizers: boolean;
}

function actionHeaderIcon(col: string, showElectronFolder: boolean): React.ReactNode {
  switch (col) {
    case "actions":
      return (
        <span
          className="inline-flex items-center justify-center gap-0"
          aria-hidden
        >
          <span className="inline-flex w-11 justify-center">
            <img
              src="/static/icons/apple-touch-icon-180.png"
              alt=""
              width={18}
              height={18}
              className="object-contain opacity-70"
              decoding="async"
            />
          </span>
          {showElectronFolder ? (
            <span className="inline-flex w-11 justify-center text-[1.25rem] leading-none opacity-70">
              📂
            </span>
          ) : null}
          <span className="inline-flex w-11 justify-center">
            <HeroHeaderLogo className="h-[22px] w-[22px] object-contain opacity-70" />
          </span>
        </span>
      );
    case "open":
      return (
        <span className="inline-flex justify-center text-[1.25rem] leading-none" aria-hidden>
          📂
        </span>
      );
    case "hero":
      return <HeroHeaderLogo className="mx-auto h-7 w-7 object-contain" />;
    case "cover_print_default":
      return <span aria-hidden="true">🖨️</span>;
    default:
      return null;
  }
}

function TimesheetDesktopThead({
  dataColumns,
  actionColumns,
  actionColWidths,
  actionRightOffsets,
  heroFields,
  showElectronFolder,
  sort,
  onSort,
  activeResize,
  onResizePointerDown,
  omitResizers,
}: TimesheetDesktopTheadProps) {
  return (
    <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {dataColumns.map((col, idx) => (
          <th
            key={col}
            scope="col"
            className="eh-data-th whitespace-normal break-words px-3 py-3 text-left font-semibold leading-snug"
          >
            <SortableHeader
              label={getTimesheetColumnLabel(col, heroFields)}
              columnKey={col}
              sort={sort}
              onSort={onSort}
            />
            {!omitResizers && (
              <span
                className={clsx("eh-data-resizer", activeResize === col && "is-active")}
                role="separator"
                aria-orientation="vertical"
                title="Spaltenbreite ziehen"
                onPointerDown={(e) => onResizePointerDown(e, col, idx)}
              />
            )}
          </th>
        ))}
        {actionColumns.map((col) => (
          <th
            key={col}
            scope="col"
            className="eh-data-action-cell whitespace-nowrap py-3 text-center text-[10px] font-semibold uppercase tracking-wide"
            style={{
              width: `${actionColWidths[col] ?? PROJECT_ACTION_COL_WIDTH}px`,
              right: `${actionRightOffsets[col]}px`,
            }}
            title={getTimesheetColumnLabel(col, heroFields)}
          >
            <span className="sr-only">{getTimesheetColumnLabel(col, heroFields)}</span>
            {actionHeaderIcon(col, showElectronFolder)}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function DesktopTable({
  rows,
  columns,
  heroFields,
  hoursUsageById,
  widths,
  user,
  loading,
  emptyText,
  showElectronFolder,
  onRowOpen,
  onContactToggled,
  onColumnWidthChange,
  sort,
  onSort,
  onSortReset,
}: {
  rows: TimesheetSummary[];
  columns: string[];
  heroFields: HeroCustomField[];
  hoursUsageById: Record<number, HoursUsageEntry>;
  widths: Record<string, number>;
  user: CurrentUser | null;
  loading: boolean;
  emptyText: string;
  showElectronFolder: boolean;
  onRowOpen: (row: TimesheetSummary) => void;
  onContactToggled?: (timesheetId: number, atContact: boolean) => void;
  onColumnWidthChange?: (key: string, width: number) => void;
  sort: { key: string; direction: "asc" | "desc" } | null;
  onSort: (key: string) => void;
  onSortReset: () => void;
}) {
  const dataColumns = useMemo(
    () => columns.filter((c) => !isTimesheetActionColumn(c)),
    [columns],
  );
  const actionColumns = useMemo(
    () => columns.filter((c) => isTimesheetActionColumn(c)),
    [columns],
  );

  const dataWidths = useMemo(() => {
    return dataColumns.map((col) => {
      const stored = Number(widths[col]);
      const defaultWidth = getTimesheetColumnDefaultWidth(col, showElectronFolder);
      return Number.isFinite(stored) && stored > 0
        ? clampTimesheetColumnWidth(stored)
        : defaultWidth;
    });
  }, [dataColumns, widths, showElectronFolder]);

  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);

  const actionColWidths = useMemo(() => {
    const widthsByKey: Record<string, number> = {};
    for (const col of actionColumns) {
      widthsByKey[col] =
        col === "actions"
          ? getTimesheetActionColWidth(showElectronFolder)
          : PROJECT_ACTION_COL_WIDTH;
    }
    return widthsByKey;
  }, [actionColumns, showElectronFolder]);

  const actionRightOffsets = useMemo(() => {
    const offsets: Record<string, number> = {};
    let right = 0;
    for (let i = actionColumns.length - 1; i >= 0; i--) {
      const key = actionColumns[i];
      if (!key) continue;
      offsets[key] = right;
      right += actionColWidths[key] ?? PROJECT_ACTION_COL_WIDTH;
    }
    return offsets;
  }, [actionColumns, actionColWidths]);

  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const floatingSyncKey = useMemo(
    () => [dataColumns.join("|"), actionColumns.join("|"), dataWidths.join(","), rows.length].join("§"),
    [dataColumns, actionColumns, dataWidths, rows.length],
  );

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: dataColumns.length > 0,
    syncKey: floatingSyncKey,
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);

    const startX = event.clientX;
    const startWidth =
      dataWidths[idx] ?? getTimesheetColumnDefaultWidth(colKey, showElectronFolder);
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampTimesheetColumnWidth(width);
      if (colEl) colEl.style.width = `${lastWidth}px`;
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) floatCol.style.width = `${lastWidth}px`;
    }

    function onMove(e: PointerEvent) {
      applyWidth(startWidth + (e.clientX - startX));
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) {
        onColumnWidthChange?.(colKey, lastWidth);
      }
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  const isAbrechnung = userIsAbrechnung(user);
  const isAnrechnung = userIsAnrechnung(user);
  const isManagement = userIsManagement(user);

  return (
    <>
      <TableSortResetBar visible={sort != null} onReset={onSortReset} />
      <EhDataTableShell ref={tableWrapRef}>
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          <colgroup>
            {dataColumns.map((col, idx) => (
              <col
                key={col}
                ref={(el) => {
                  colRefs.current[col] = el;
                }}
                style={{ width: `${dataWidths[idx]}px` }}
              />
            ))}
            {actionColumns.map((col) => (
              <col
                key={col}
                style={{
                  width: `${actionColWidths[col] ?? PROJECT_ACTION_COL_WIDTH}px`,
                }}
              />
            ))}
          </colgroup>

          <TimesheetDesktopThead
            dataColumns={dataColumns}
            actionColumns={actionColumns}
            actionColWidths={actionColWidths}
            actionRightOffsets={actionRightOffsets}
            heroFields={heroFields}
            showElectronFolder={showElectronFolder}
            sort={sort}
            onSort={onSort}
            activeResize={activeResize}
            onResizePointerDown={startResize}
            omitResizers={false}
          />

          <tbody className="divide-y divide-separator bg-card">
            {rows.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-label-tertiary"
                >
                  {emptyText}
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const status = getTimesheetStatus(row);
              const highlightBilling = shouldHighlightBillingQuestionRow(user, row);
              const isArchived = status === "archived";
              return (
                <tr
                  key={row.id}
                  onClick={() => onRowOpen(row)}
                  className={clsx(
                    "eh-data-row cursor-pointer align-middle transition",
                    highlightBilling && "bg-status-red/5",
                    isArchived && "opacity-70",
                  )}
                  title={isArchived ? "Archiviert" : undefined}
                >
                  {dataColumns.map((col) => (
                    <td
                      key={col}
                      className="eh-data-body-cell min-w-0 align-middle whitespace-normal break-words px-3 py-3 text-label"
                      onClick={
                        col === "at_contact" ? (e) => e.stopPropagation() : undefined
                      }
                    >
                      <CellContent
                        col={col}
                        row={row}
                        user={user}
                        heroFields={heroFields}
                        hoursUsageById={hoursUsageById}
                        isAbrechnung={isAbrechnung}
                        isAnrechnung={isAnrechnung}
                        isManagement={isManagement}
                        onContactToggled={onContactToggled}
                      />
                    </td>
                  ))}
                  {actionColumns.map((col) => (
                    <td
                      key={col}
                      className="eh-data-action-cell align-middle p-0 text-center leading-none"
                      style={{
                        width: `${actionColWidths[col] ?? PROJECT_ACTION_COL_WIDTH}px`,
                        right: `${actionRightOffsets[col]}px`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TimesheetActionCell
                        col={col}
                        row={row}
                        showElectronFolder={showElectronFolder}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </EhDataTableShell>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{
              top: floating.top,
              left: floating.left,
              width: floating.width,
            }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <colgroup>
                  {dataColumns.map((col, idx) => (
                    <col
                      key={col}
                      ref={(el) => {
                        floatingColRefs.current[col] = el;
                      }}
                      style={{ width: `${dataWidths[idx]}px` }}
                    />
                  ))}
                  {actionColumns.map((col) => (
                    <col
                      key={col}
                      style={{
                        width: `${actionColWidths[col] ?? PROJECT_ACTION_COL_WIDTH}px`,
                      }}
                    />
                  ))}
                </colgroup>
                <TimesheetDesktopThead
                  dataColumns={dataColumns}
                  actionColumns={actionColumns}
                  actionColWidths={actionColWidths}
                  actionRightOffsets={actionRightOffsets}
                  heroFields={heroFields}
                  showElectronFolder={showElectronFolder}
                  sort={sort}
                  onSort={onSort}
                  activeResize={activeResize}
                  onResizePointerDown={startResize}
                  omitResizers={false}
                />
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function getTimesheetSortValue(
  row: TimesheetSummary,
  col: string,
  heroFields: HeroCustomField[],
): string | number | boolean | null | undefined {
  switch (col) {
    case "number":
      return getTimesheetDisplayNumber(row);
    case "project":
      return formatTimesheetProjectLabel(row);
    case "contact":
      return String(row.ansprechpartner || row.ansprechpartner_nachname || "").trim();
    case "period":
      return formatTimesheetPeriod(row);
    case "hours":
      return row.total_hours == null ? null : Number(row.total_hours);
    case "status":
      return timesheetStatusLabel(getTimesheetStatus(row));
    case "at_contact":
      return Boolean(row.at_contact);
    case "invoice_number":
      return row.invoice_number;
    case "invoice_value":
      return row.invoice_value == null ? null : Number(row.invoice_value);
    default:
      return renderTimesheetProjectCellValue(row, col, heroFields).value;
  }
}

function CellContent({
  col,
  row,
  user,
  heroFields,
  hoursUsageById,
  isAbrechnung,
  isAnrechnung,
  isManagement,
  onContactToggled,
}: {
  col: string;
  row: TimesheetSummary;
  user: CurrentUser | null;
  heroFields: HeroCustomField[];
  hoursUsageById: Record<number, HoursUsageEntry>;
  isAbrechnung: boolean;
  isAnrechnung: boolean;
  isManagement: boolean;
  onContactToggled?: (timesheetId: number, atContact: boolean) => void;
}) {
  const status = getTimesheetStatus(row);
  const order = timesheetRowAsOrder(row);

  switch (col) {
    case "number":
      return (
        <NumberCell
          row={row}
          status={status}
          user={user}
          isAbrechnung={isAbrechnung}
          isAnrechnung={isAnrechnung}
          isManagement={isManagement}
        />
      );
    case "project":
      return <span className="break-words">{formatTimesheetProjectLabel(row) || "—"}</span>;
    case "contact":
      return (
        <span>
          {String(row.ansprechpartner || row.ansprechpartner_nachname || "").trim() || "—"}
        </span>
      );
    case "period":
      return <span>{formatTimesheetPeriod(row) || "—"}</span>;
    case "hours":
      return (
        <span className="tabular-nums">
          {row.total_hours != null
            ? Number(row.total_hours).toLocaleString("de-DE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "—"}
        </span>
      );
    case "status":
      return (
        <TimesheetBadge className={statusBadgeClass(status, user)}>
          {timesheetStatusLabel(status)}
        </TimesheetBadge>
      );
    case "at_contact":
      return (
        <AtContactCell row={row} status={status} user={user} onToggled={onContactToggled} />
      );
    case "invoice_number":
      return <span>{row.invoice_number || "—"}</span>;
    case "invoice_value":
      return <span className="tabular-nums">{formatInvoiceValue(row.invoice_value)}</span>;
    case "hours_usage":
      return <HoursUsageCell entry={hoursUsageById[order.id]} />;
    default: {
      const cell = renderTimesheetProjectCellValue(row, col, heroFields);
      if (cell.badges?.length) {
        return (
          <span className="flex flex-wrap gap-1">
            {cell.badges.map((badge: ProjectTimesheetBadge) => (
              <TimesheetBadge
                key={badge.label}
                className={statusBadgeClass(normalizeTimesheetStatus(badge.status), user)}
              >
                {badge.label}
              </TimesheetBadge>
            ))}
          </span>
        );
      }
      if (cell.mono) {
        return <span className="font-mono text-sm">{cell.value ?? "—"}</span>;
      }
      return <span className="break-words">{cell.value ?? "—"}</span>;
    }
  }
}

function NumberCell({
  row,
  status,
  user,
  isAbrechnung,
  isAnrechnung,
  isManagement,
}: {
  row: TimesheetSummary;
  status: string;
  user: CurrentUser | null;
  isAbrechnung: boolean;
  isAnrechnung: boolean;
  isManagement: boolean;
}) {
  const displayNumber = getTimesheetDisplayNumber(row) || "—";
  const badgeClass = statusBadgeClass(status, user);
  const billingQuestionVariant = getBillingQuestionBadgeClass(row, isManagement);
  const showRueckfrageBadge =
    (isAbrechnung || isAnrechnung || isManagement) && shouldShowBillingQuestionBadge(row);
  const newBillingBadge =
    isAbrechnung && status === "signed" && row.is_new_for_billing === true;

  return (
    <span className="flex flex-wrap items-center gap-1">
      <TimesheetBadge className={badgeClass}>{displayNumber}</TimesheetBadge>
      {row.has_newer_revision ? (
        <TimesheetBadge subtle>überarbeitet</TimesheetBadge>
      ) : null}
      {row.signed_outdated_revision ? (
        <TimesheetBadge warning>veraltete Unterschrift</TimesheetBadge>
      ) : null}
      {newBillingBadge ? <TimesheetBadge warning>Neu</TimesheetBadge> : null}
      {showRueckfrageBadge && billingQuestionVariant ? (
        <TimesheetBadge
          className={billingQuestionVariant}
          title={
            hasOpenBillingQuestions(row)
              ? "Offene Rückfrage"
              : "Rückfrage beantwortet"
          }
        >
          Rückfrage
        </TimesheetBadge>
      ) : null}
    </span>
  );
}

function AtContactCell({
  row,
  status,
  user,
  onToggled,
}: {
  row: TimesheetSummary;
  status: string;
  user: CurrentUser | null;
  onToggled?: (timesheetId: number, atContact: boolean) => void;
}) {
  const contactAge = getContactAgeBadgeConfig(row);
  const billingLogin = String(row.billing_assignee_login || "").trim();
  const billingBadge =
    userIsBuero(user) && billingLogin ? (
      <TimesheetBadge compact className="bg-fill-tertiary text-label-secondary">
        {billingLogin}
      </TimesheetBadge>
    ) : null;

  if (status !== "ready") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {billingBadge ?? <span className="text-sm text-label-tertiary">—</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="checkbox"
        className="h-4 w-4 shrink-0 rounded border-separator"
        checked={Boolean(row.at_contact)}
        aria-label="Beim Ansprechpartner"
        onChange={async (e) => {
          const next = e.target.checked;
          const previous = Boolean(row.at_contact);
          onToggled?.(row.id, next);
          try {
            await markTimesheetAtContact(row.id, next);
          } catch (err) {
            onToggled?.(row.id, previous);
            console.warn("[Stundennachweise] mark-at-contact failed:", err);
          }
        }}
      />
      {contactAge ? (
        <TimesheetBadge compact className={contactAge.className}>
          {contactAge.label}
        </TimesheetBadge>
      ) : null}
      {billingBadge}
    </div>
  );
}

function TimesheetActionCell({
  col,
  row,
  showElectronFolder,
}: {
  col: string;
  row: TimesheetSummary;
  showElectronFolder: boolean;
}) {
  const isDark = useIsDarkTheme();
  const { user } = useCurrentUser();
  const { open: openProjectOverview } = useProjectOverview();
  const order = timesheetRowAsOrder(row);

  if (col === "actions") {
    const hasFolder = timesheetHasFolder(row);
    const pid = resolveTimesheetHeroProjectId(row);
    const legacy = row.legacy_import === true;
    const heroSrc = legacy
      ? isDark
        ? HERO_LEGACY_LOGO_DARK
        : HERO_LEGACY_LOGO_LIGHT
      : isDark
        ? HERO_LOGO_DARK
        : HERO_LOGO_LIGHT;

    return (
      <div className="inline-flex items-center justify-center">
        <ProjectOverviewButton
          auftragId={row.auftrag_id}
          className="eh-data-action-btn shrink-0 rounded-lg border-0 bg-transparent shadow-none"
        />
        {showElectronFolder ? (
          hasFolder ? (
            <button
              type="button"
              className="eh-data-action-btn shrink-0"
              title="Ordner öffnen"
              aria-label="Ordner öffnen"
              onClick={(e) => void handleOpenFolder(e, row)}
            >
              <span className="text-[1.25rem] leading-none" aria-hidden>
                📂
              </span>
            </button>
          ) : (
            <span
              title="Kein Projektordner hinterlegt"
              className="eh-data-action-btn inline-flex shrink-0 cursor-default items-center justify-center"
            >
              <span className="text-[1.25rem] leading-none opacity-35 grayscale" aria-hidden>
                📂
              </span>
            </span>
          )
        ) : null}
        {pid ? (
          <a
            href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(pid))}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Hero-Projekt öffnen"
            className="eh-data-action-btn shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={heroSrc}
              alt="Hero"
              style={{ width: 22, height: 22, objectFit: "contain" }}
            />
          </a>
        ) : (
          <span
            title="Kein Hero-Projekt hinterlegt"
            className="eh-data-action-btn inline-flex shrink-0 cursor-default items-center justify-center"
            style={{ opacity: 0.5 }}
          >
            <img
              src={isDark ? HERO_LEGACY_LOGO_DARK : HERO_LEGACY_LOGO_LIGHT}
              alt="Hero"
              style={{ width: 22, height: 22, objectFit: "contain" }}
            />
          </span>
        )}
      </div>
    );
  }

  if (col === "hero") {
    const pid = order.hero_project_id;
    const legacy = order.legacy_import === true;
    const heroSrc = legacy
      ? isDark
        ? HERO_LEGACY_LOGO_DARK
        : HERO_LEGACY_LOGO_LIGHT
      : isDark
        ? HERO_LOGO_DARK
        : HERO_LOGO_LIGHT;
    if (pid) {
      return (
        <a
          href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(pid))}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Hero-Projekt öffnen"
          className="eh-data-action-btn shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <img src={heroSrc} alt="Hero" style={{ width: 22, height: 22, objectFit: "contain" }} />
        </a>
      );
    }
    return (
      <span
        title="Kein Hero-Projekt hinterlegt"
        className="eh-data-action-btn inline-flex shrink-0 cursor-default items-center justify-center"
        style={{ opacity: 0.5 }}
      >
        <img
          src={isDark ? HERO_LEGACY_LOGO_DARK : HERO_LEGACY_LOGO_LIGHT}
          alt="Hero"
          style={{ width: 22, height: 22, objectFit: "contain" }}
        />
      </span>
    );
  }

  if (col === "open") {
    const hasFolder = timesheetHasFolder(row);
    if (!hasFolder) {
      if (userIsBuero(user) && isElectronWithOpenFolder()) {
        return (
          <button
            type="button"
            className="eh-data-action-btn shrink-0"
            title="Ordner suchen oder anlegen"
            onClick={(e) => {
              e.stopPropagation();
              if (order.id) openProjectOverview(order.id);
            }}
          >
            <span className="text-[1.1rem] leading-none" aria-hidden>
              ➕
            </span>
          </button>
        );
      }
      return (
        <span
          className="eh-data-action-btn inline-flex shrink-0 cursor-default items-center justify-center"
          title="Kein Projektordner hinterlegt"
        >
          <span className="text-[1.25rem] leading-none opacity-35 grayscale" aria-hidden>
            📂
          </span>
        </span>
      );
    }
    return (
      <button
        type="button"
        className="eh-data-action-btn shrink-0"
        title="Ordner öffnen"
        aria-label="Ordner öffnen"
        onClick={(e) => void handleOpenFolder(e, row)}
      >
        <span className="text-[1.25rem] leading-none" aria-hidden>
          📂
        </span>
      </button>
    );
  }

  if (col === "cover_print_default") {
    return (
      <button
        type="button"
        className="eh-data-action-btn shrink-0"
        title="Deckblatt über Standarddrucker drucken"
        onClick={(e) => {
          e.stopPropagation();
          if (order.id) {
            window.location.href = `/auftraege.html?auftrag_id=${order.id}&action=cover_print`;
          }
        }}
      >
        🖨️
      </button>
    );
  }

  return null;
}

function useIsDarkTheme(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() => readIsDark());
  useEffect(() => {
    const root = document.documentElement;
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() => setIsDark(readIsDark()));
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme", "class"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function readIsDark(): boolean {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === "dark") return true;
  if (root.classList.contains("dark")) return true;
  return false;
}

async function handleOpenFolder(e: React.MouseEvent, row: TimesheetSummary) {
  e.stopPropagation();
  const path = getTimesheetFolderPath(row);
  if (!path) {
    if (row.auftrag_id) {
      const webUrl = await getAuftragSharepointWebUrl(Number(row.auftrag_id));
      if (webUrl) window.open(webUrl, "_blank", "noopener");
    }
    return;
  }
  const openFolder = window.envAPI?.openFolder;
  if (typeof openFolder !== "function") return;
  const err = await openFolder(path);
  if (err) {
    if (row.auftrag_id) {
      const webUrl = await getAuftragSharepointWebUrl(Number(row.auftrag_id));
      if (webUrl) {
        window.open(webUrl, "_blank", "noopener");
        return;
      }
    }
    toast.error(String(err));
  }
}

function MobileCards({
  rows,
  user,
  loading,
  emptyText,
  onRowOpen,
}: {
  rows: TimesheetSummary[];
  user: CurrentUser | null;
  loading: boolean;
  emptyText: string;
  onRowOpen: (row: TimesheetSummary) => void;
}) {
  if (!rows.length && !loading) {
    return <p className="py-8 text-center text-sm text-label-tertiary">{emptyText}</p>;
  }

  return (
    <ul className="divide-y divide-separator rounded-ios-lg bg-card ring-1 ring-separator/35">
      {rows.map((row) => {
        const status = getTimesheetStatus(row);
        return (
          <li key={row.id}>
            <button
              type="button"
              className="flex w-full flex-col gap-1 px-4 py-3 text-left transition hover:bg-fill-tertiary/50"
              onClick={() => onRowOpen(row)}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-label">
                  {getTimesheetDisplayNumber(row) || "—"}
                </span>
                <TimesheetBadge className={statusBadgeClass(status, user)}>
                  {timesheetStatusLabel(status)}
                </TimesheetBadge>
              </div>
              <span className="text-sm text-label">{formatTimesheetProjectLabel(row)}</span>
              <span className="text-xs text-label-secondary">
                {[formatTimesheetPeriod(row), row.total_hours != null ? `${row.total_hours} h` : ""]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
